import { oc } from "@orpc/contract";
import { EngagementHistoryPointSchema as DomainHistoryPointSchema, EngagementQuestionSchema, EngagementSubMetricSchema } from "@domains/engagement/shared/engagementMetricTypes.ts";
import { EngagementRatingSchema } from "@domains/engagement/shared/engagementRating.ts";
import { EngagementEnpsHistoryPointSchema, EnpsBandSchema } from "@domains/engagement/shared/enps.ts";
import { TimeFrameSchema } from "@domains/engagement/shared/timeFrame.ts";
import { ReportingScopeSchema } from "@packages/core/organization/scope.ts";
import { z } from "zod";

const EngagementInputSchema = z.object({
    scope: ReportingScopeSchema.optional(),
    timeFrame: TimeFrameSchema.optional()
});

const GetEnpsInputSchema = EngagementInputSchema.extend({
    includeTrend: z.boolean().optional()
});

const EngagementHistoryPointSchema = z.object({
    date: z.string(),
    score: z.number().nullable(),
    organizationScore: z.number().nullable()
});
export type EngagementHistoryPoint = z.infer<typeof EngagementHistoryPointSchema>;

const EngagementHistorySchema = z.object({
    score: z.number().nullable(),
    scoreRating: EngagementRatingSchema.nullable(),
    scoreDelta: z.number().nullable(),
    organizationScore: z.number().nullable(),
    organizationScoreDelta: z.number().nullable(),
    points: z.array(EngagementHistoryPointSchema)
});
export type EngagementHistory = z.infer<typeof EngagementHistorySchema>;

const EngagementEnpsSchema = z.object({
    isActive: z.boolean(),
    score: z.number().nullable(),
    scoreBand: EnpsBandSchema.nullable(),
    deactivatedSince: z.string().nullable(),
    trendPoints: z.array(EngagementEnpsHistoryPointSchema).optional()
});
export type EngagementEnps = z.infer<typeof EngagementEnpsSchema>;

const EngagementEnpsHistorySchema = z.object({
    points: z.array(EngagementEnpsHistoryPointSchema)
});
export type EngagementEnpsHistory = z.infer<typeof EngagementEnpsHistorySchema>;

const GetParticipationInputSchema = EngagementInputSchema.extend({
    includeTrend: z.boolean().optional()
});

const EngagementParticipationPointSchema = z.object({
    date: z.string(),
    rate: z.number(),
    companyRate: z.number().nullable()
});
export type EngagementParticipationPoint = z.infer<typeof EngagementParticipationPointSchema>;

const EngagementParticipationSchema = z.object({
    rate: z.number(),
    delta: z.number().nullable(),
    respondedCount: z.number(),
    invitedCount: z.number(),
    band: EngagementRatingSchema,
    companyRate: z.number().nullable(),
    companyDelta: z.number().nullable(),
    trendPoints: z.array(EngagementParticipationPointSchema).optional()
});
export type EngagementParticipation = z.infer<typeof EngagementParticipationSchema>;

export const METRIC_SORT_COLUMNS = ["name", "score", "scoreDelta"] as const;
export type MetricSortColumn = (typeof METRIC_SORT_COLUMNS)[number];

const EngagementMetricSchema = z.object({
    id: z.string(),
    label: z.string(),
    description: z.string(),
    score: z.number().nullable(),
    scoreRating: EngagementRatingSchema.nullable(),
    scoreDelta: z.number().nullable(),
    subMetrics: z.array(EngagementSubMetricSchema)
});
export type EngagementMetric = z.infer<typeof EngagementMetricSchema>;

const EngagementMetricInputSchema = EngagementInputSchema.extend({
    metricId: z.string()
});

const GetQuestionResultsInputSchema = EngagementInputSchema.extend({
    metricId: z.string().optional()
});

const GetMetricDetailInputSchema = EngagementMetricInputSchema.extend({
    includeTrend: z.boolean().optional()
});

const EngagementMetricDetailSchema = z.object({
    id: z.string(),
    label: z.string(),
    description: z.string(),
    score: z.number().nullable(),
    scoreRating: EngagementRatingSchema.nullable(),
    scoreDelta: z.number().nullable(),
    subMetrics: z.array(EngagementSubMetricSchema),
    trendPoints: z.array(DomainHistoryPointSchema).optional()
});
export type EngagementMetricDetail = z.infer<typeof EngagementMetricDetailSchema>;

const EngagementQuestionResultsSchema = z.object({
    questions: z.array(EngagementQuestionSchema)
});
export type EngagementQuestionResults = z.infer<typeof EngagementQuestionResultsSchema>;

const GetCustomQuestionSummaryInputSchema = EngagementInputSchema.extend({
    questionId: z.string()
});

const EngagementCustomQuestionScoreHistoryPointSchema = z.object({
    date: z.string(),
    score: z.number().nullable(),
    organizationScore: z.number().nullable()
});

const EngagementCustomQuestionDistributionItemSchema = z.object({
    label: z.string(),
    count: z.number(),
    value: z.number().nullable()
});

const EngagementCustomQuestionSummarySchema = z.object({
    id: z.string(),
    questionLabel: z.string(),
    isPaused: z.boolean(),
    createdByName: z.string().optional(),
    updatedAt: z.string(),
    score: z.number().nullable(),
    scoreRating: EngagementRatingSchema.nullable(),
    scoreDelta: z.number().nullable(),
    organizationScore: z.number().nullable(),
    organizationScoreDelta: z.number().nullable(),
    trendPoints: z.array(EngagementCustomQuestionScoreHistoryPointSchema),
    participationRate: z.number(),
    invitedCount: z.number(),
    distribution: z.object({
        totalAnswerCount: z.number(),
        items: z.array(EngagementCustomQuestionDistributionItemSchema)
    })
});
export type EngagementCustomQuestionSummary = z.infer<typeof EngagementCustomQuestionSummarySchema>;

const GetInsightInputSchema = EngagementInputSchema.extend({
    requirePromptContext: z.boolean().optional()
});

const EngagementInsightSchema = z.object({
    insightsAvailable: z.boolean(),
    headline: z.string().nullable(),
    bullets: z.array(z.string()),
    generatedAt: z.string(),
    promptContext: z.string().nullable()
});
export type EngagementInsight = z.infer<typeof EngagementInsightSchema>;

const SurveyReminderScopeInputSchema = EngagementInputSchema.pick({ scope: true });

const SendSurveyReminderInputSchema = SurveyReminderScopeInputSchema.extend({
    customMessage: z.string().optional()
});

const SurveyReminderAudienceCountSchema = z.object({
    inactiveMemberCount: z.number()
});
export type SurveyReminderAudienceCount = z.infer<typeof SurveyReminderAudienceCountSchema>;

export const HEATMAP_BENCHMARK_INDUSTRIES = [
    "All",
    "NaturalResources",
    "UtilitiesAndServices",
    "Construction",
    "Manufacturing",
    "WholesaleAndRetailTrade",
    "TransportationAndWarehousing",
    "InformationAndCulturalIndustries",
    "FinancialServicesOrInsurance",
    "RealEstateAndRentalOrLeasing",
    "ProfessionalScientificTechnicalServices",
    "ManagementConsulting",
    "EducationalServices",
    "HealthcareAndSocialAssistance",
    "ArtsEntertainmentAndRecreation",
    "AccommodationAndFoodServices",
    "PublicAndGovernmentAdministration",
    "ComputerSoftwareItServices",
    "NonprofitOrganization",
    "MarketingAndAdvertising"
] as const;
export const HEATMAP_BENCHMARK_LOCATIONS = ["All", "APAC", "AustraliaAndNewZealand", "Canada", "Europe", "LatinAmerica", "MiddleEastAndAfrica", "NorthAmerica", "UnitedStates"] as const;
export const HEATMAP_BENCHMARK_COMPANY_SIZES = ["AllRanges", "OneTo99", "OneHundredTo249", "TwoHundredFiftyTo999", "OneThousandAndMore"] as const;

const HeatmapBenchmarkIndustrySchema = z.enum(HEATMAP_BENCHMARK_INDUSTRIES);
const HeatmapBenchmarkLocationSchema = z.enum(HEATMAP_BENCHMARK_LOCATIONS);
const HeatmapBenchmarkCompanySizeSchema = z.enum(HEATMAP_BENCHMARK_COMPANY_SIZES);

const HeatmapBenchmarkSelectionSchema = z.object({
    industry: HeatmapBenchmarkIndustrySchema,
    location: HeatmapBenchmarkLocationSchema,
    companySize: HeatmapBenchmarkCompanySizeSchema
});
export type HeatmapBenchmarkSelection = z.infer<typeof HeatmapBenchmarkSelectionSchema>;

const HeatmapInputSchema = z.object({
    scope: ReportingScopeSchema.optional(),
    timeFrame: TimeFrameSchema.optional(),
    date: z.string().optional(),
    benchmark: HeatmapBenchmarkSelectionSchema.optional()
});

const HeatmapScoreSchema = z.object({
    value: z.number().nullable(),
    absoluteValue: z.number().nullable()
});
export type HeatmapScore = z.infer<typeof HeatmapScoreSchema>;

const HeatmapSubMetricColumnSchema = z.object({
    id: z.string(),
    label: z.string(),
    score: HeatmapScoreSchema
});
export type HeatmapSubMetricColumn = z.infer<typeof HeatmapSubMetricColumnSchema>;

const HeatmapMetricColumnSchema = z.object({
    id: z.string(),
    label: z.string(),
    score: HeatmapScoreSchema,
    subMetrics: z.array(HeatmapSubMetricColumnSchema)
});
export type HeatmapMetricColumn = z.infer<typeof HeatmapMetricColumnSchema>;

const HeatmapParticipationSchema = z.object({
    rate: HeatmapScoreSchema,
    activeMemberCount: z.number(),
    totalMemberCount: z.number()
});
export type HeatmapParticipationColumn = z.infer<typeof HeatmapParticipationSchema>;

const HeatmapManagerSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    jobTitle: z.string().nullable(),
    imageUrl: z.string().nullable()
});
export type HeatmapManager = z.infer<typeof HeatmapManagerSchema>;

const HeatmapRowSchema = z.object({
    groupId: z.string(),
    manager: HeatmapManagerSchema,
    isHidden: z.boolean(),
    participation: HeatmapParticipationSchema,
    enps: HeatmapScoreSchema,
    score: HeatmapScoreSchema,
    metrics: z.array(HeatmapMetricColumnSchema)
});
export type HeatmapRow = z.infer<typeof HeatmapRowSchema>;

const HeatmapOrganizationRowSchema = z.object({
    participation: HeatmapParticipationSchema,
    enps: HeatmapScoreSchema,
    score: HeatmapScoreSchema,
    metrics: z.array(HeatmapMetricColumnSchema)
});
export type HeatmapOrganizationRow = z.infer<typeof HeatmapOrganizationRowSchema>;

const HeatmapBenchmarkSubMetricSchema = z.object({ id: z.string(), label: z.string(), score: z.number().nullable() });
const HeatmapBenchmarkMetricSchema = z.object({ id: z.string(), label: z.string(), score: z.number().nullable(), subMetrics: z.array(HeatmapBenchmarkSubMetricSchema) });
const HeatmapBenchmarkRowSchema = z.object({
    participationRate: z.number().nullable(),
    enps: z.number().nullable(),
    score: z.number().nullable(),
    metrics: z.array(HeatmapBenchmarkMetricSchema)
});
export type HeatmapBenchmarkRow = z.infer<typeof HeatmapBenchmarkRowSchema>;

const HeatmapResponseSchema = z.object({
    organization: HeatmapOrganizationRowSchema.nullable(),
    benchmark: HeatmapBenchmarkRowSchema.nullable(),
    rows: z.array(HeatmapRowSchema)
});
export type HeatmapResponse = z.infer<typeof HeatmapResponseSchema>;

const EngagementManagerContextSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    jobTitle: z.string().nullable()
});
export type EngagementManagerContext = z.infer<typeof EngagementManagerContextSchema>;

const EngagementDateRangeSchema = z.object({
    from: z.string(),
    to: z.string()
});

const ExportHeatmapScoreSchema = z.tuple([z.number().nullable(), z.number().nullable()]);

const ExportHeatmapMetricSchema = z.object({
    id: z.string(),
    score: ExportHeatmapScoreSchema
});

const ExportHeatmapManagerSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    jobTitle: z.string().nullable()
});

const ExportHeatmapParticipationSchema = z.object({
    rate: ExportHeatmapScoreSchema,
    activeMemberCount: z.number(),
    totalMemberCount: z.number()
});

const ExportHeatmapRowSchema = z.object({
    groupId: z.string(),
    manager: ExportHeatmapManagerSchema,
    isHidden: z.boolean(),
    participation: ExportHeatmapParticipationSchema,
    enps: ExportHeatmapScoreSchema,
    score: ExportHeatmapScoreSchema,
    metrics: z.array(ExportHeatmapMetricSchema)
});
export type ExportHeatmapRow = z.infer<typeof ExportHeatmapRowSchema>;

const ExportHeatmapOrganizationRowSchema = z.object({
    participation: ExportHeatmapParticipationSchema,
    enps: ExportHeatmapScoreSchema,
    score: ExportHeatmapScoreSchema,
    metrics: z.array(ExportHeatmapMetricSchema)
});
export type ExportHeatmapOrganizationRow = z.infer<typeof ExportHeatmapOrganizationRowSchema>;

const ExportHeatmapSchema = z.object({
    organization: ExportHeatmapOrganizationRowSchema.nullable(),
    rows: z.array(ExportHeatmapRowSchema)
});
export type ExportHeatmap = z.infer<typeof ExportHeatmapSchema>;

const EngagementReportExportContextSchema = z.object({
    scope: ReportingScopeSchema,
    timeFrame: TimeFrameSchema.optional(),
    dateRange: EngagementDateRangeSchema,
    language: z.string().optional(),
    latestScore: EngagementHistorySchema,
    enps: EngagementEnpsSchema,
    participation: EngagementParticipationSchema,
    metrics: z.array(EngagementMetricSchema),
    questions: z.object({
        standard: EngagementQuestionResultsSchema,
        custom: EngagementQuestionResultsSchema
    }),
    heatmap: ExportHeatmapSchema,
    managerContext: EngagementManagerContextSchema.nullable()
});
export type EngagementReportExportContext = z.infer<typeof EngagementReportExportContextSchema>;

export const ENGAGEMENT_EXPORT_PATH = "/api/analytics/engagement/export";
export const ENGAGEMENT_QUESTIONS_EXPORT_PATH = "/api/analytics/engagement/questions/export";
export const ENGAGEMENT_HEATMAP_EXPORT_PATH = "/api/analytics/engagement/heatmap/export";

const EngagementExportFormatSchema = z.enum(["Csv", "Excel"]);
export type EngagementExportFormat = z.infer<typeof EngagementExportFormatSchema>;

export const ExportEngagementInputSchema = EngagementInputSchema.extend({ format: EngagementExportFormatSchema });

export const ExportEngagementQuestionsInputSchema = EngagementInputSchema.extend({ format: EngagementExportFormatSchema });

export const ExportEngagementHeatmapInputSchema = HeatmapInputSchema.omit({ benchmark: true }).extend({ format: EngagementExportFormatSchema });

export const engagementContract = {
    getHistory: oc.route({ method: "GET", path: "/history" }).errors({ UPSTREAM_FAILURE: {} }).input(EngagementInputSchema).output(EngagementHistorySchema),
    getEnps: oc.route({ method: "GET", path: "/enps" }).errors({ UPSTREAM_FAILURE: {} }).input(GetEnpsInputSchema).output(EngagementEnpsSchema),
    getParticipation: oc.route({ method: "GET", path: "/participation" }).errors({ UPSTREAM_FAILURE: {} }).input(GetParticipationInputSchema).output(EngagementParticipationSchema),
    listMetrics: oc.route({ method: "GET", path: "/metrics" }).errors({ UPSTREAM_FAILURE: {} }).input(EngagementInputSchema).output(z.array(EngagementMetricSchema)),
    getMetricDetail: oc.route({ method: "GET", path: "/metric-detail" }).errors({ NOT_FOUND: {}, UPSTREAM_FAILURE: {} }).input(GetMetricDetailInputSchema).output(EngagementMetricDetailSchema),
    getQuestionResults: oc.route({ method: "GET", path: "/question-results" }).errors({ UPSTREAM_FAILURE: {} }).input(GetQuestionResultsInputSchema).output(EngagementQuestionResultsSchema),
    getCustomQuestionResults: oc.route({ method: "GET", path: "/custom-question-results" }).errors({ UPSTREAM_FAILURE: {} }).input(EngagementInputSchema).output(EngagementQuestionResultsSchema),
    getCustomQuestionSummary: oc.route({ method: "GET", path: "/custom-question-summary" }).errors({ NOT_FOUND: {}, UPSTREAM_FAILURE: {} }).input(GetCustomQuestionSummaryInputSchema).output(EngagementCustomQuestionSummarySchema),
    getInsight: oc.route({ method: "GET", path: "/insight" }).errors({ UPSTREAM_FAILURE: {} }).input(GetInsightInputSchema).output(EngagementInsightSchema),
    getSurveyReminderAudienceCount: oc.route({ method: "GET", path: "/survey-reminders/count" }).errors({ UPSTREAM_FAILURE: {} }).input(SurveyReminderScopeInputSchema).output(SurveyReminderAudienceCountSchema),
    sendSurveyReminder: oc.route({ method: "POST", path: "/survey-reminders" }).errors({ UPSTREAM_FAILURE: {} }).input(SendSurveyReminderInputSchema).output(z.void()),
    getHeatmap: oc.route({ method: "GET", path: "/heatmap" }).errors({ UPSTREAM_FAILURE: {} }).input(HeatmapInputSchema).output(HeatmapResponseSchema),
    getEngagementReportExportContext: oc.route({ method: "GET", path: "/report-export-context" }).errors({ UPSTREAM_FAILURE: {} }).input(EngagementInputSchema).output(EngagementReportExportContextSchema)
};
